// AI assisted development

import { Injectable } from '@angular/core';
import { ContextInfo } from '../../@shared/models/context-info';
import { environment } from '../../../environments/environment';
import { toPendoAccount, toPendoInitializePayload } from '../config/pendo-field-mapper';

const PENDO_SCRIPT_ID = 'pendo-agent-script';
const PENDO_CDN_HOST = 'cdn.pendo.io/agent/static/';

@Injectable()
export class PendoService {

  isConfigured(): boolean {
    if (environment.pendoEnabled === false) {
      return false;
    }
    const key = environment.pendoApiKey;
    return typeof key === 'string' && key.trim().length > 0;
  }

  loadAgent(): Promise<void> {
    if (!this.isConfigured()) {
      return Promise.resolve();
    }
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return Promise.resolve();
    }
    if (this.isAgentPresent()) {
      return Promise.resolve();
    }

    const apiKey = environment.pendoApiKey.trim();
    this.installSnippetStub(apiKey);
    return this.appendAgentScript(apiKey);
  }

  initialize(context: ContextInfo): void {
    try {
      if (!this.canCallPendo() || !context) {
        return;
      }
      const language = this.getAppSetLanguage();
      const payload = toPendoInitializePayload(context, language || '');
      if (!payload.visitor.id) {
        return;
      }
      window.pendo.initialize(payload);
    } catch (err) {
      // Analytics must never break login or session restore.
    }
  }

  updateAccount(context: ContextInfo): void {
    try {
      if (!this.canCallPendo() || !context) {
        return;
      }
      const account = toPendoAccount(context);
      if (!account) {
        return;
      }
      window.pendo.updateOptions({ account: account });
    } catch (err) {
      // Analytics must never break in-session account updates.
    }
  }

  notifyPageChange(): void {
    try {
      if (!this.canCallPendo()) {
        return;
      }
      if (typeof window.pendo.pageLoad === 'function') {
        window.pendo.pageLoad();
      }
    } catch (err) {
      // Analytics must never break navigation.
    }
  }

  teardown(): void {
    try {
      if (!this.canCallPendo()) {
        return;
      }
      if (typeof window.pendo.teardown === 'function') {
        window.pendo.teardown();
      }
    } catch (err) {
      // Swallow teardown errors so logout / login never fail because of Pendo.
    }
  }

  /**
   * Same locale source as TranslationService.getFirstBrowserLanguage().
   * Inlined to avoid a ContextService → PendoService → TranslationService → ProxyService cycle.
   */
  private getAppSetLanguage(): string {
    if (typeof window === 'undefined' || !window.navigator) {
      return '';
    }
    const nav: Navigator = window.navigator;
    const browserLanguagePropertyKeys = ['language', 'browserLanguage', 'systemLanguage', 'userLanguage'];
    for (let i = 0; i < browserLanguagePropertyKeys.length; i++) {
      const language = nav[browserLanguagePropertyKeys[i]];
      if (language && language.length) {
        return language;
      }
    }
    return '';
  }

  private canCallPendo(): boolean {
    return this.isConfigured() && typeof window !== 'undefined' && !!window.pendo;
  }

  private isAgentPresent(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }
    if (document.getElementById(PENDO_SCRIPT_ID)) {
      return true;
    }
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      const src = scripts[i].src;
      if (src && src.indexOf(PENDO_CDN_HOST) !== -1) {
        return true;
      }
    }
    return false;
  }

  /**
   * Official Pendo install snippet stub so initialize/identify/updateOptions/pageLoad
   * can queue before pendo.js finishes loading. Does not expose a track() method on this service.
   */
  private installSnippetStub(apiKey: string): void {
    const p = window;
    const o = p.pendo || (p.pendo = { _q: [] } as any);
    if (!o._q) {
      o._q = [];
    }
    const methods = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
    for (let w = 0; w < methods.length; w++) {
      (function (m: string) {
        if (typeof o[m] === 'function') {
          return;
        }
        o[m] = function () {
          const args = [m].concat(Array.prototype.slice.call(arguments, 0));
          o._q[m === 'initialize' ? 'unshift' : 'push'](args);
        };
      })(methods[w]);
    }
    // apiKey is used when appending the script; stub only prepares window.pendo
    if (!apiKey) {
      return;
    }
  }

  private appendAgentScript(apiKey: string): Promise<void> {
    return new Promise<void>((resolve) => {
      const y = document.createElement('script');
      y.async = true;
      y.id = PENDO_SCRIPT_ID;
      y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
      const scripts = document.getElementsByTagName('script');
      const z = scripts.length > 0 ? scripts[0] : document.head || document.body;
      if (z && z.parentNode) {
        z.parentNode.insertBefore(y, z);
      } else if (document.head) {
        document.head.appendChild(y);
      }
      // Resolve immediately so APP_INITIALIZER does not block app boot on the Pendo CDN.
      resolve();
    });
  }
}
